import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Handshake, PlayCircle, BookOpen, Wrench, Clock, Gift, Star, MessageCircle, ShieldCheck, Headset } from 'lucide-react';
import { Button, GlassCard, SectionHeading, Badge } from '../components/ui';
import Seo from '../components/Seo';
import { getStaticMeta } from '../seo/pageMeta';
import { ARTICLES, getArticleCopy } from '../data/articles';

const ResourcesScreen = () => {
  const { t, i18n } = useTranslation();
  const meta = getStaticMeta('resources', i18n.language);

  const propFirms = [
    {
      key: 'takeprofit',
      icon: 'chart',
    },
    {
      key: 'lucidtrading',
      icon: 'trophy',
      popular: true,
    },
    {
      key: 'apextradefunding',
      icon: 'rocket',
    },
  ];

  return (
    <div className="min-h-screen bg-bg text-body pt-28 pb-24 md:pt-32">
      <Seo title={meta.title} description={meta.description} path="/resources" />
      <div className="mx-auto max-w-page px-6">
        <SectionHeading
          index={t('nav.resources')}
          title={t('resources.title')}
          subtitle={t('resources.subtitle')}
          className="mb-14"
        />

      <div className="space-y-10">
        {/* Prop Firms Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <GlassCard hover={false} className="p-8">
            <div className="flex items-center mb-6 gap-4">
              <Handshake className="w-8 h-8 text-primary shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-body">{t('resources.propFirms.title')}</h2>
                <p className="text-muted">{t('resources.propFirms.subtitle')}</p>
              </div>
            </div>
            <p className="text-muted mb-8 text-lg">{t('resources.propFirms.description')}</p>
            
            {/* Quick Comparison Table */}
            <GlassCard hover={false} className="p-6 mb-8 overflow-x-auto">
              <h3 className="text-body font-semibold mb-4">
                {t('resources.propFirms.quickComparison')}
              </h3>
              <div className="min-w-full">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="font-medium text-muted text-xs uppercase tracking-wider">Prop Firm</div>
                  <div className="font-medium text-muted text-xs uppercase tracking-wider text-center">{t('resources.propFirms.daysToPass')}</div>
                  <div className="font-medium text-muted text-xs uppercase tracking-wider text-center">{t('resources.propFirms.maxCapital')}</div>
                  <div className="font-medium text-muted text-xs uppercase tracking-wider text-center">{t('resources.propFirms.drawdownType')}</div>
                  
                  <div className="text-body font-medium">{t('resources.propFirms.takeprofit.name')}</div>
                  <div className="text-center text-primary font-medium">{t('resources.propFirms.takeprofit.daysToPass')} {t('resources.propFirms.days')}</div>
                  <div className="text-center text-body">{t('resources.propFirms.takeprofit.maxCapital')}</div>
                  <div className="text-center text-body">{t('resources.propFirms.takeprofit.drawdown')}</div>
                  
                  <div className="text-body font-medium flex items-center gap-2">
                    {t('resources.propFirms.lucidtrading.name')}
                    <Badge tone="cyan">{t('resources.propFirms.popular')}</Badge>
                  </div>
                  <div className="text-center text-success font-medium">{t('resources.propFirms.lucidtrading.daysToPass')} {t('resources.propFirms.days')}</div>
                  <div className="text-center text-primary font-medium">{t('resources.propFirms.lucidtrading.maxCapital')}</div>
                  <div className="text-center text-success font-medium">{t('resources.propFirms.lucidtrading.drawdown')}</div>
                  
                  <div className="text-body font-medium">{t('resources.propFirms.apextradefunding.name')}</div>
                  <div className="text-center text-success font-medium">{t('resources.propFirms.apextradefunding.daysToPass')} {t('resources.propFirms.days')}</div>
                  <div className="text-center text-primary font-medium">{t('resources.propFirms.apextradefunding.maxCapital')}</div>
                  <div className="text-center text-body">{t('resources.propFirms.apextradefunding.drawdown')}</div>
                </div>
              </div>
            </GlassCard>
            
            {/* Prop Firms Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {propFirms.map(({ key, popular }) => (
                <GlassCard key={key} className="p-6 relative overflow-hidden">
                  <div className="absolute top-4 right-4">
                    <Badge tone={popular ? 'cyan' : 'neutral'}>
                      {popular ? t('resources.propFirms.popular') : t(`resources.propFirms.${key}.badge`)}
                    </Badge>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-btn border border-white/[.08] bg-bg mx-auto">
                      <Handshake className="h-4 w-4 text-primary" />
                    </div>
                    <h3 className="mb-3 text-lg font-semibold text-body">{t(`resources.propFirms.${key}.name`)}</h3>
                    <p className="text-muted text-sm mb-4">
                      {t(`resources.propFirms.${key}.description`)}
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <h4 className="text-body font-medium text-sm mb-3">
                      {t('resources.propFirms.specifications')}
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-white/[.04] rounded-btn p-3 border border-white/[.06]">
                        <div className="mb-1">
                          <span className="text-muted">{t('resources.propFirms.daysToPass')}</span>
                        </div>
                        <span className="text-body font-medium">{t(`resources.propFirms.${key}.daysToPass`)} {t('resources.propFirms.days')}</span>
                      </div>
                      
                      <div className="bg-white/[.04] rounded-btn p-3 border border-white/[.06]">
                        <div className="mb-1">
                          <span className="text-muted">{t('resources.propFirms.maxCapital')}</span>
                        </div>
                        <span className="text-body font-medium">{t(`resources.propFirms.${key}.maxCapital`)}</span>
                      </div>
                      
                      <div className="bg-white/[.04] rounded-btn p-3 border border-white/[.06]">
                        <div className="mb-1">
                          <span className="text-muted">{t('resources.propFirms.profitSplit')}</span>
                        </div>
                        <span className="text-body font-medium">{t(`resources.propFirms.${key}.profitSplit`)}</span>
                      </div>
                      
                      <div className="bg-white/[.04] rounded-btn p-3 border border-white/[.06]">
                        <div className="mb-1">
                          <span className="text-muted">{t('resources.propFirms.drawdownType')}</span>
                        </div>
                        <span className="text-body font-medium">{t(`resources.propFirms.${key}.drawdown`)}</span>
                      </div>
                    </div>
                    
                    <div className="bg-white/[.04] rounded-btn p-3 border border-white/[.06]">
                      <div className="mb-1">
                        <span className="text-muted">{t('resources.propFirms.consistency')}</span>
                      </div>
                      <span className="text-body font-medium">{t(`resources.propFirms.${key}.consistency`)}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-body font-medium text-sm mb-3">
                      {t('resources.propFirms.benefits')}
                    </h4>
                    <ul className="space-y-2 text-xs">
                      {t(`resources.propFirms.${key}.highlights`, { returnObjects: true }).map((highlight, index) => (
                        <li key={index} className="flex items-center text-muted">
                          <span className="text-primary/70 mr-2">✓</span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    href={t(`resources.propFirms.${key}.url`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('resources.propFirms.getStarted')}
                  </Button>
                </GlassCard>
              ))}
            </div>

            {/* Exclusive Offer CTA */}
            <GlassCard hover={false} className="mt-12 p-8 border-primary/20">
              <div className="text-center">
                <div className="flex justify-center items-center mb-4 gap-3">
                  <Gift className="w-8 h-8 text-primary shrink-0" />
                  <h3 className="text-xl font-semibold text-body">{t('resources.propFirms.exclusiveOffer')}</h3>
                </div>
                <p className="text-muted mb-6 max-w-2xl mx-auto">
                  {t('resources.propFirms.exclusiveOfferDescription')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button to="/support">
                    <Star className="w-4 h-4" />
                    {t('resources.propFirms.discoverBenefits')}
                  </Button>
                  <Button variant="secondary" to="/support">
                    <MessageCircle className="w-4 h-4" />
                    {t('resources.propFirms.contactUs')}
                  </Button>
                </div>

                <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-sm text-muted">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                    {t('resources.propFirms.verifiedPartners')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-primary shrink-0" />
                    {t('resources.propFirms.negotiatedConditions')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Headset className="w-4 h-4 text-primary shrink-0" />
                    {t('resources.propFirms.dedicatedSupport')}
                  </div>
                </div>
              </div>
            </GlassCard>
          </GlassCard>
        </motion.section>

        {/* Practical Guides Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <GlassCard hover={false} className="p-8">
            <div className="flex items-center mb-6 gap-4">
              <PlayCircle className="w-8 h-8 text-accent shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-body">{t('resources.practicalGuides.title')}</h2>
                <p className="text-muted">{t('resources.practicalGuides.subtitle')}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <GlassCard hover={false} className="p-4">
                <div className="relative w-full mb-3" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/NU8qSAOdAyY"
                    title={t('resources.practicalGuides.thirdPartyLicensing.title')}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <h3 className="text-body font-semibold text-sm mb-1">
                  {t('resources.practicalGuides.thirdPartyLicensing.title')}
                </h3>
                <p className="text-muted text-xs">
                  {t('resources.practicalGuides.thirdPartyLicensing.description')}
                </p>
              </GlassCard>
            </div>

            <div className="text-center py-6 border-t border-white/[.06]">
              <p className="text-muted text-sm flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                {t('resources.practicalGuides.comingSoonDescription')}
              </p>
            </div>
          </GlassCard>
        </motion.section>

        {/* Trading Education Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <GlassCard hover={false} className="p-8">
            <div className="flex items-center mb-6 gap-4">
              <BookOpen className="w-8 h-8 text-primary shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-body">{t('resources.tradingEducation.title')}</h2>
                <p className="text-muted">{t('resources.tradingEducation.subtitle')}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ARTICLES.map((article) => {
                const copy = getArticleCopy(article, i18n.language);
                return (
                  <GlassCard key={article.slug} className="flex flex-col p-6">
                    <h3 className="mb-3 text-lg font-semibold text-body">
                      {copy.title}
                    </h3>
                    <p className="mb-6 flex-1 text-sm text-muted">
                      {copy.summary}
                    </p>
                    <Button to={`/resources/${article.slug}`} variant="secondary" className="self-start text-sm">
                      {t('resources.tradingEducation.readArticle')}
                    </Button>
                  </GlassCard>
                );
              })}
            </div>
          </GlassCard>
        </motion.section>

        {/* Templates & Tools Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <GlassCard hover={false} className="p-8">
            <div className="flex items-center mb-6 gap-4">
              <Wrench className="w-8 h-8 text-accent shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-body">{t('resources.toolsTemplates.title')}</h2>
                <p className="text-muted">{t('resources.toolsTemplates.subtitle')}</p>
              </div>
            </div>
            
            <div className="text-center py-12">
              <Clock className="mx-auto mb-4 h-5 w-5 text-muted" />
              <h3 className="mb-4 text-lg font-semibold text-body">{t('resources.toolsTemplates.comingSoon')}</h3>
              <p className="text-muted max-w-2xl mx-auto mb-8">
                {t('resources.toolsTemplates.comingSoonDescription')}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <GlassCard hover={false} className="p-6 opacity-60">
                  <div className="flex items-start space-x-4 text-left">
                    <div className="w-16 h-16 bg-accent/20 rounded-card flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-body mb-2">{t('resources.toolsTemplates.freeTemplates')}</h4>
                      <p className="text-muted mb-4 text-sm">{t('resources.toolsTemplates.freeTemplatesDescription')}</p>
                      <Button variant="secondary" disabled className="text-sm">
                        {t('resources.toolsTemplates.comingSoon')}
                      </Button>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard hover={false} className="p-6 opacity-60">
                  <div className="flex items-start space-x-4 text-left">
                    <div className="w-16 h-16 bg-accent/20 rounded-card flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-body mb-2">{t('resources.toolsTemplates.tradingTools')}</h4>
                      <p className="text-muted mb-4 text-sm">{t('resources.toolsTemplates.tradingToolsDescription')}</p>
                      <Button variant="secondary" disabled className="text-sm">
                        {t('resources.toolsTemplates.comingSoon')}
                      </Button>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </div>
          </GlassCard>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center"
        >
          <GlassCard hover={false} className="p-8 border-primary/20">
            <h3 className="mb-4 text-xl font-semibold tracking-tight text-body">
              {t('resources.cta.title')}
            </h3>
            <p className="text-muted mb-6 max-w-2xl mx-auto">
              {t('resources.cta.description')}
            </p>
            <Button to="/support">
              {t('resources.cta.button')}
            </Button>
          </GlassCard>
        </motion.section>
      </div>
      </div>
    </div>
  );
};

export default ResourcesScreen;
